// API error classes + response-error normalization. Extracted from api/client.ts
// (verbatim). `parseError` is shared by request.ts + docBlob.ts.
import type { LimitExceededPayload } from '../types';

export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export class LimitExceededError extends ApiError {
  payload: LimitExceededPayload;
  constructor(payload: LimitExceededPayload) {
    super(payload.upgrade_hint || 'Plan limit reached', 402, 'limit_exceeded');
    this.name = 'LimitExceededError';
    this.payload = payload;
  }
}

function genericMessage(status: number): string {
  if (status >= 500) return 'Something went wrong on our end. Please try again.';
  if (status === 429) return 'Too many requests. Please slow down and try again.';
  if (status === 413) return 'That is too large to save.';
  if (status === 404) return 'Not found.';
  return 'Request failed. Please try again.';
}

export async function parseError(res: Response): Promise<ApiError> {
  let detail: unknown;
  try {
    const text = await res.text();
    if (text) detail = (JSON.parse(text) as { detail?: unknown }).detail;
  } catch {
    /* non-JSON body — fall through to a generic message */
  }

  // Structured 402: the LimitExceeded payload rides in `detail`.
  if (
    res.status === 402 &&
    detail &&
    typeof detail === 'object' &&
    (detail as { error?: unknown }).error === 'limit_exceeded'
  ) {
    return new LimitExceededError(detail as LimitExceededPayload);
  }
  // Other structured refusals: detail = { error, detail } (e.g. archived).
  if (detail && typeof detail === 'object' && typeof (detail as { error?: unknown }).error === 'string') {
    const code = (detail as { error: string }).error;
    const inner = (detail as { detail?: unknown }).detail;
    const msg = typeof inner === 'string' && inner ? inner : genericMessage(res.status);
    return new ApiError(msg, res.status, code);
  }
  // Plain string detail < 500: the backend is enumeration-safe, so these strings
  // are intentionally user-facing (e.g. "invalid credentials"). 5xx never leaks.
  if (res.status < 500 && typeof detail === 'string' && detail) {
    return new ApiError(detail, res.status);
  }
  return new ApiError(genericMessage(res.status), res.status);
}
